function cloneValue(value) {
    if (value === undefined) return undefined;
    if (value === null) return null;
    try {
        return JSON.parse(JSON.stringify(value));
    } catch (_error) {
        return value;
    }
}

function createLocalSectionId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `preset-section-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeSections(sections = []) {
    return sections.map((section, index) => ({
        ...section,
        index
    }));
}

function replaceProjectSections(commit, rootState, nextSections) {
    const currentProject = rootState.project || buildDefaultProject();
    commit("replaceProject", {
        ...currentProject,
        sections: normalizeSections(nextSections)
    }, { root: true });
}

function buildDefaultProject() {
    return {
        _id: "preset-editor-sandbox",
        type: "caseStudy",
        settings: {
            globalSpacingEnabled: false,
            globalSpacing: 60
        },
        designSettings: {
            globalColorStyles: true,
            globalFontStyles: true
        },
        sections: []
    };
}

export const presetEditor = {
    namespaced: true,
    state() {
        return {
            active: false,
            snapshot: null
        };
    },
    getters: {
        isActive(state) {
            return state.active;
        }
    },
    mutations: {
        setActive(state, value) {
            state.active = value;
        },
        setSnapshot(state, snapshot) {
            state.snapshot = snapshot;
        }
    },
    actions: {
        startSession({ state, commit, rootState, rootGetters }, payload = {}) {
            if (state.active) return;

            const snapshot = {
                project: cloneValue(rootState.project),
                portfolio: cloneValue(rootState.portfolio),
                selectedSection: cloneValue(rootState.selectedSection),
                viewData: cloneValue(rootGetters["view/viewData"])
            };

            const draftProject = payload.project || buildDefaultProject();
            const draftPortfolio = payload.portfolio || cloneValue(rootState.portfolio);
            const viewData = snapshot.viewData || {};

            commit("replaceProject", draftProject, { root: true });
            commit("setPortfolio", draftPortfolio, { root: true });
            commit("selectSection", null, { root: true });
            commit("view/updateView", {
                ...viewData,
                project: draftProject,
                portfolio: draftPortfolio
            }, { root: true });

            commit("setSnapshot", snapshot);
            commit("setActive", true);
        },
        stopSession({ state, commit }) {
            if (!state.active || !state.snapshot) {
                commit("setActive", false);
                commit("setSnapshot", null);
                return;
            }

            const snapshot = state.snapshot;
            commit("replaceProject", snapshot.project ?? null, { root: true });
            commit("setPortfolio", snapshot.portfolio ?? null, { root: true });
            commit("selectSection", snapshot.selectedSection ?? null, { root: true });
            commit("view/updateView", snapshot.viewData || {}, { root: true });

            commit("setSnapshot", null);
            commit("setActive", false);
        },
        syncSections({ state, rootState, commit }, sections = []) {
            if (!state.active) return;
            replaceProjectSections(commit, rootState, sections);
        },
        addSectionLocal({ state, rootState, commit }, { postData, modal = null } = {}) {
            if (!state.active) return;
            const section = postData?.section;
            if (!section) return;

            const sections = cloneValue(rootState.project?.sections || []);
            const insertIndex = Number.isInteger(postData?.index)
                ? Math.max(0, Math.min(postData.index, sections.length))
                : sections.length;

            const nextSection = cloneValue(section);
            if (!nextSection._id) {
                nextSection._id = createLocalSectionId();
            }
            delete nextSection.deletedAt;

            sections.splice(insertIndex, 0, nextSection);
            replaceProjectSections(commit, rootState, sections);
            commit("selectSection", nextSection, { root: true });
            commit("setActiveModal", modal || null, { root: true });
        },
        updateSectionLocal({ state, commit, rootState }, { section, modal = null } = {}) {
            if (!state.active || !section?._id) return;
            commit("updateSection", section, { root: true });
            if (
                (modal && modal === "keepModalOpen") ||
                rootState.activeModal === "SubscriptionExpiredModal"
            ) {
                return;
            }
            commit("setActiveModal", modal || null, { root: true });
        },
        updateSectionsLocal({ state, rootState, commit }, { sections } = {}) {
            if (!state.active || !Array.isArray(sections)) return;
            replaceProjectSections(commit, rootState, sections);
        },
        archiveSectionLocal({ state, rootState, commit }, { sectionIndex, sectionId } = {}) {
            if (!state.active) return;
            const sections = cloneValue(rootState.project?.sections || []);
            let resolvedIndex = Number.isInteger(sectionIndex) ? sectionIndex : -1;
            if (resolvedIndex < 0 && sectionId) {
                resolvedIndex = sections.findIndex((section) => section?._id === sectionId);
            }
            if (resolvedIndex < 0 || resolvedIndex >= sections.length) return;

            const [removed] = sections.splice(resolvedIndex, 1);
            replaceProjectSections(commit, rootState, sections);
            if (removed?._id && rootState.selectedSection?._id === removed._id) {
                commit("selectSection", null, { root: true });
            }
            commit("setActiveModal", null, { root: true });
        },
        reorderSectionsLocal({ state, rootState, commit }, sections) {
            if (!state.active || !Array.isArray(sections)) return;
            replaceProjectSections(commit, rootState, sections);
            commit("setActiveModal", null, { root: true });
        },
        duplicateSectionLocal({ state, rootState, commit }, { sectionId } = {}) {
            if (!state.active || !sectionId) return;
            const sections = cloneValue(rootState.project?.sections || []);
            const sourceIndex = sections.findIndex((section) => section._id === sectionId);
            if (sourceIndex < 0) return;

            const duplicated = cloneValue(sections[sourceIndex]);
            duplicated._id = createLocalSectionId();
            delete duplicated.deletedAt;
            delete duplicated.copyStatus;

            const insertIndex = sourceIndex + 1;
            sections.splice(insertIndex, 0, duplicated);

            replaceProjectSections(commit, rootState, sections);
            commit("selectSection", duplicated, { root: true });
            commit("setActiveModal", null, { root: true });
        }
    }
};
